import { supabase } from '../lib/supabase'

export type TestimonialType = 'text' | 'image' | 'video' | 'youtube' | 'instagram'

export interface Testimonial {
  id: string
  type: TestimonialType
  content: string // For text, image URL, video URL, YouTube ID, Instagram URL
  author_name?: string
  author_location?: string
  created_at: string
  updated_at: string
  deleted_at?: string | null
  order?: number // For ordering testimonials
}

export interface CreateTestimonialData {
  type: TestimonialType
  content: string
  author_name?: string
  author_location?: string
  order?: number
}

export interface UpdateTestimonialData extends Partial<CreateTestimonialData> {
  id: string
}

export class TestimonialService {
  // Create a new testimonial
  static async createTestimonial(testimonialData: CreateTestimonialData): Promise<{ success: boolean; data?: Testimonial; error?: string }> {
    try {
      const testimonialId = crypto.randomUUID()
      
      const { data, error } = await supabase
        .from('testimonials')
        .insert([{ ...testimonialData, id: testimonialId }])
        .select()
        .single()

      if (error) {
        return { success: false, error: error.message }
      }

      const transformedTestimonial = this.transformSupabaseTestimonialToTestimonial(data)
      return { success: true, data: transformedTestimonial }
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to create testimonial' 
      }
    }
  }

  // Update an existing testimonial
  static async updateTestimonial(testimonialData: UpdateTestimonialData): Promise<{ success: boolean; data?: Testimonial; error?: string }> {
    try {
      const { id, ...updateData } = testimonialData
      
      const { data, error } = await supabase
        .from('testimonials')
        .update(updateData)
        .eq('id', id)
        .select()
        .single()

      if (error) {
        return { success: false, error: error.message }
      }

      const transformedTestimonial = this.transformSupabaseTestimonialToTestimonial(data)
      return { success: true, data: transformedTestimonial }
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to update testimonial' 
      }
    }
  }

  // Soft delete a testimonial
  static async deleteTestimonial(testimonialId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('testimonials')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', testimonialId)

      if (error) {
        return { success: false, error: error.message }
      }

      return { success: true }
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to delete testimonial' 
      }
    }
  }

  // Permanently delete a testimonial
  static async permanentlyDeleteTestimonial(testimonialId: string, contentUrl?: string): Promise<{ success: boolean; error?: string }> {
    try {
      // Delete associated image/video from storage if it's an uploaded file
      if (contentUrl && (contentUrl.includes('supabase') || contentUrl.includes('storage'))) {
        const { deleteImage } = await import('../lib/storage')
        await deleteImage(contentUrl).catch(err => {
          console.warn('Failed to delete media:', contentUrl, err)
        })
      }

      const { error } = await supabase
        .from('testimonials')
        .delete()
        .eq('id', testimonialId)

      if (error) {
        return { success: false, error: error.message }
      }

      return { success: true }
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to permanently delete testimonial' 
      }
    }
  }

  // Restore a soft-deleted testimonial
  static async restoreTestimonial(testimonialId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('testimonials')
        .update({ deleted_at: null })
        .eq('id', testimonialId)

      if (error) {
        return { success: false, error: error.message }
      }

      return { success: true }
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to restore testimonial' 
      }
    }
  }

  // Get all testimonials (non-deleted by default)
  static async getTestimonials(includeDeleted: boolean = false): Promise<{ success: boolean; data?: Testimonial[]; error?: string }> {
    try {
      let query = supabase
        .from('testimonials')
        .select('*')
        .order('order', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: false })

      if (!includeDeleted) {
        query = query.is('deleted_at', null)
      }

      const { data, error } = await query

      if (error) {
        return { success: false, error: error.message }
      }

      const transformedTestimonials = (data || []).map(item => this.transformSupabaseTestimonialToTestimonial(item))
      return { success: true, data: transformedTestimonials }
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to fetch testimonials' 
      }
    }
  }

  // Transform Supabase testimonial data to our Testimonial interface
  private static transformSupabaseTestimonialToTestimonial(supabaseTestimonial: any): Testimonial {
    return {
      id: supabaseTestimonial.id,
      type: supabaseTestimonial.type,
      content: supabaseTestimonial.content,
      author_name: supabaseTestimonial.author_name,
      author_location: supabaseTestimonial.author_location,
      created_at: supabaseTestimonial.created_at,
      updated_at: supabaseTestimonial.updated_at,
      deleted_at: supabaseTestimonial.deleted_at,
      order: supabaseTestimonial.order
    }
  }
}



